import React from 'react';
import {
  FEATURE_CATALOG,
  FEATURE_CATEGORIES,
  featureLabel,
  type FeatureCatalogEntry,
} from '../constants/featureCatalog';
import { PRICING_TIERS, type PricingTierId } from '../constants/pricingTiers';
import { getAttribution } from '../marketingAttribution';
import DodTermText from '../DodTermText';
import { type DodGlossaryTermId } from '../dodGlossary';
import SubscriptionTeamPanel from './SubscriptionTeamPanel';
import { FeatureMark } from './FeatureMark';
import './SubscriptionCatalogPanel.css';
import { scrollElementWithinConsole } from '../consoleScroll';

type SubscriptionLevel = {
  level_id: string;
  name: string;
  monthly_price_usd: number | null;
  entitlements: string[];
  canonical?: boolean;
};

type SubscriptionMemberPreview = {
  email: string;
  job?: string;
  invite_status?: string;
};

type PayingSubscription = {
  subscription_id: string;
  company_id: number | null;
  company_name: string;
  email: string;
  tier_id: string;
  tier_name: string;
  status: string;
  current_period_end: string;
  cancel_at_period_end?: boolean;
  member_count?: number;
  pending_invite_count?: number;
  seat_count?: number;
  members?: SubscriptionMemberPreview[];
  source: string;
  entitlement_features?: string[];
  job?: string;
};

type PanelMode = 'levels' | 'paying' | 'expired' | 'mine';

type SubscriptionCatalogPanelProps = {
  mode: PanelMode;
  language: 'en' | 'es';
  gatewayBaseUrl?: string;
  accessToken?: string;
  onAccessDenied?: () => void;
};

const copy = {
  en: {
    levelsTitle: 'Subscription levels',
    levelsHelp: 'These are the plans a paying account can be on. Toggle NextCompli features on each plan. Saving a plan updates active accounts on that plan.',
    payingTitle: 'Paying subscriptions',
    payingHelp: 'The subscription is the commercial record. People belong on the account in Access Management. Ending a plan removes features and keeps the Admin so they can still sign in.',
    expiredTitle: 'Expired subscriptions',
    expiredHelp: 'Accounts whose plan has ended or whose payment is past due.',
    name: 'Name',
    price: 'Monthly price (USD)',
    id: 'Level id',
    save: 'Save level',
    delete: 'Delete',
    account: 'Account',
    plan: 'Plan',
    email: 'Billing contact',
    status: 'Status',
    renews: 'Renews',
    colAccount: 'Account',
    colPlan: 'Plan',
    colRenews: 'Renews on',
    colMembers: 'Members',
    colStatus: 'Status',
    membersEmpty: 'None yet',
    membersPending: (n: number) => `${n} pending`,
    membersPendingOne: 'Pending invite',
    membersMore: (n: number) => `+${n} more`,
    emptyLevels: 'No subscription levels yet.',
    emptyPaying: 'No paying subscriptions yet.',
    emptyExpired: 'No expired subscriptions.',
    loadError: 'Could not load this list.',
    saved: 'Saved.',
    canonical: 'Published catalog plan — edit name, price, and features. It cannot be deleted.',
    backfill: 'No card was charged. This account was entered by hand.',
    add: 'Add a level',
    edit: 'Edit',
    cancelEdit: 'Cancel',
    endNow: 'Expire now',
    endPeriod: 'End at renewal',
    accountName: 'Account name',
    adminEmail: 'Billing contact',
    planChoice: 'Plan',
    saveAccount: 'Save subscription',
    newAccount: 'Add subscription',
    rbacMissing: 'Saved the account. That email is not a user yet, so no Admin role was added.',
    featuresTitle: 'Included features',
    featuresHelp: 'Each feature maps to a permission (feature.<key>.use). The company entitlement is the billing gate; product seats inherit the matching permission.',
    featuresCount: (n: number) => `${n} feature${n === 1 ? '' : 's'}`,
    people: 'Members',
    managePeople: 'Manage people',
    accountSettings: 'Account settings',
    addPerson: 'Add a person',
    addShort: 'Add',
    settingsShort: 'Settings',
    peopleHelp: 'People belong on the subscription. Invite by email, pick Admin / Contributor / Viewer, and resend if needed. Pending invites are not members until accepted.',
    endingSoon: 'Ends at renewal',
    active: 'Active',
    expired: 'Expired',
    expireReason: 'Reason (optional)',
    expireConfirmNow: 'Remove plan features now and move this account to Expired.',
    expireConfirmPeriod: 'Keep access until the renewal date, then expire automatically.',
    confirmExpire: 'Confirm expire',
    resynced: (n: number) => `Updated ${n} active account${n === 1 ? '' : 's'} on this plan.`,
    contactPrice: 'Contact',
    perMonth: 'per month',
    details: 'Account details',
    changePlan: 'Change plan',
    suspendedLabel: 'Suspended',
    suspend: 'Suspend',
    resume: 'Resume',
    planChanged: 'Plan updated. A higher plan starts now. A lower plan removes features that are not on the new plan. People and companies stay.',
    suspendedNotice: 'Suspended. Plan features are off until you resume. People and companies stay.',
    resumedNotice: 'Resumed. Plan features are on again.',
    planHelp: 'Upgrades and downgrades start now. Suspending turns features off and keeps the account.',
    mineTitle: 'mySubscriptions',
    searchAccounts: 'Search accounts',
    searchEmpty: 'No accounts match that search.',
    showMore: 'Show more',
    showingCount: (shown: number, total: number) => `Showing ${shown} of ${total}`,
    emptyMine: 'You are not on a subscription account yet.',
    yourJob: 'Your job',
    jobAdmin: 'Subscription admin',
    jobContributor: 'Contributor',
    jobViewer: 'Viewer',
    mineHelp: 'Open an account to see features. Subscription admins can upgrade the plan or open Stripe billing.',
    upgradePlan: 'Upgrade plan',
    manageBilling: 'Manage billing',
    upgradeTo: (name: string) => `Upgrade to ${name}`,
    currentPlan: 'Current plan',
    contactQuote: 'Contact for quote',
    openingBilling: 'Opening…',
    mineBillingViewer: 'Only a subscription admin can change the plan for this account.',
  },
  es: {
    levelsTitle: 'Niveles de suscripción',
    levelsHelp: 'Estos son los planes de una cuenta de pago. Active o quite funciones de NextCompli en cada plan. Guardar actualiza las cuentas activas de ese plan.',
    payingTitle: 'Suscripciones de pago',
    payingHelp: 'La suscripción es el registro comercial. Las personas viven en la cuenta en Gestión de acceso. Terminar el plan quita funciones y conserva al Admin para que pueda entrar.',
    expiredTitle: 'Suscripciones vencidas',
    expiredHelp: 'Cuentas cuyo plan terminó o cuyo pago está atrasado.',
    name: 'Nombre',
    price: 'Precio mensual (USD)',
    id: 'Id del nivel',
    save: 'Guardar nivel',
    delete: 'Eliminar',
    account: 'Cuenta',
    plan: 'Plan',
    email: 'Contacto de facturación',
    status: 'Estado',
    renews: 'Renueva',
    colAccount: 'Cuenta',
    colPlan: 'Plan',
    colRenews: 'Renueva el',
    colMembers: 'Miembros',
    colStatus: 'Estado',
    membersEmpty: 'Ninguno aún',
    membersPending: (n: number) => `${n} pendiente${n === 1 ? '' : 's'}`,
    membersPendingOne: 'Invitación pendiente',
    membersMore: (n: number) => `+${n} más`,
    emptyLevels: 'Todavía no hay niveles.',
    emptyPaying: 'Todavía no hay suscripciones de pago.',
    emptyExpired: 'No hay suscripciones vencidas.',
    loadError: 'No se pudo cargar esta lista.',
    saved: 'Guardado.',
    canonical: 'Plan publicado del catálogo: puede editar nombre, precio y funciones. No se puede eliminar.',
    backfill: 'No se cobró ninguna tarjeta. Esta cuenta se anotó a mano.',
    add: 'Agregar un nivel',
    edit: 'Editar',
    cancelEdit: 'Cancelar',
    endNow: 'Vencer ahora',
    endPeriod: 'Terminar en la renovación',
    accountName: 'Nombre de la cuenta',
    adminEmail: 'Contacto de facturación',
    planChoice: 'Plan',
    saveAccount: 'Guardar suscripción',
    newAccount: 'Agregar suscripción',
    rbacMissing: 'Se guardó la cuenta. Ese correo todavía no es un usuario, así que no se agregó el rol de Admin.',
    featuresTitle: 'Funciones incluidas',
    featuresHelp: 'Cada función tiene un permiso (feature.<clave>.use). La función de la empresa es la puerta de facturación; los puestos de producto heredan el permiso.',
    featuresCount: (n: number) => `${n} función${n === 1 ? '' : 'es'}`,
    people: 'Miembros',
    managePeople: 'Gestionar personas',
    accountSettings: 'Ajustes de la cuenta',
    addPerson: 'Agregar una persona',
    addShort: 'Agregar',
    settingsShort: 'Ajustes',
    peopleHelp: 'Las personas viven en la suscripción. Invite por correo, elija Admin / Colaborador / Lector y reenvíe si hace falta. Las invitaciones pendientes no son miembros hasta que se acepten.',
    endingSoon: 'Termina en la renovación',
    active: 'Activa',
    expired: 'Vencida',
    expireReason: 'Motivo (opcional)',
    expireConfirmNow: 'Quitar las funciones del plan ahora y pasar esta cuenta a Vencidas.',
    expireConfirmPeriod: 'Mantener el acceso hasta la fecha de renovación y luego vencer.',
    confirmExpire: 'Confirmar vencimiento',
    resynced: (n: number) => `Se actualizaron ${n} cuenta${n === 1 ? '' : 's'} activa${n === 1 ? '' : 's'} de este plan.`,
    contactPrice: 'Contacto',
    perMonth: 'al mes',
    details: 'Detalles de la cuenta',
    changePlan: 'Cambiar plan',
    suspendedLabel: 'Suspendida',
    suspend: 'Suspender',
    resume: 'Reanudar',
    planChanged: 'Plan actualizado. Un plan mayor empieza ahora. Un plan menor quita las funciones que no están en el plan nuevo. Las personas y las empresas se quedan.',
    suspendedNotice: 'Suspendida. Las funciones del plan quedan apagadas hasta que la reanude. Las personas y las empresas se quedan.',
    resumedNotice: 'Reanudada. Las funciones del plan vuelven a estar activas.',
    planHelp: 'Las subidas y bajadas empiezan ahora. Suspender apaga las funciones y conserva la cuenta.',
    mineTitle: 'mySubscriptions',
    searchAccounts: 'Buscar cuentas',
    searchEmpty: 'Ninguna cuenta coincide con esa búsqueda.',
    showMore: 'Ver más',
    showingCount: (shown: number, total: number) => `Mostrando ${shown} de ${total}`,
    emptyMine: 'Todavía no está en una cuenta de suscripción.',
    yourJob: 'Su puesto',
    jobAdmin: 'Admin de suscripción',
    jobContributor: 'Colaborador',
    jobViewer: 'Lector',
    mineHelp: 'Abra una cuenta para ver funciones. Los admins de suscripción pueden mejorar el plan o abrir la facturación de Stripe.',
    upgradePlan: 'Mejorar plan',
    manageBilling: 'Administrar facturación',
    upgradeTo: (name: string) => `Mejorar a ${name}`,
    currentPlan: 'Plan actual',
    contactQuote: 'Solicitar cotización',
    openingBilling: 'Abriendo…',
    mineBillingViewer: 'Solo un admin de suscripción puede cambiar el plan de esta cuenta.',
  },
};

const formatMoney = (value: number | null | undefined, language: 'en' | 'es', contactLabel: string) => {
  if (value == null || Number.isNaN(Number(value))) {
    return contactLabel;
  }
  return new Intl.NumberFormat(language === 'es' ? 'es-US' : 'en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(Number(value));
};

const formatRenewal = (iso: string, language: 'en' | 'es') => {
  if (!iso) {
    return '—';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return new Intl.DateTimeFormat(language === 'es' ? 'es-US' : 'en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
};

const SubscriptionCatalogPanel: React.FC<SubscriptionCatalogPanelProps> = ({
  mode,
  language,
  gatewayBaseUrl,
  accessToken,
  onAccessDenied,
}) => {
  const text = copy[language];
  const apiBase = String(gatewayBaseUrl || '').replace(/\/$/, '');
  const onAccessDeniedRef = React.useRef(onAccessDenied);
  onAccessDeniedRef.current = onAccessDenied;
  const [levels, setLevels] = React.useState<SubscriptionLevel[]>([]);
  const [features, setFeatures] = React.useState<FeatureCatalogEntry[]>(FEATURE_CATALOG);
  const [rows, setRows] = React.useState<PayingSubscription[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);
  const [draftId, setDraftId] = React.useState('');
  const [draftName, setDraftName] = React.useState('');
  const [draftPrice, setDraftPrice] = React.useState('');
  const [draftEntitlements, setDraftEntitlements] = React.useState<string[]>([]);
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [showLevelEditor, setShowLevelEditor] = React.useState(false);
  const [accountName, setAccountName] = React.useState('');
  const [adminEmail, setAdminEmail] = React.useState('');
  const [planId, setPlanId] = React.useState('basic');
  const [editingSubscriptionId, setEditingSubscriptionId] = React.useState<string | null>(null);
  const [showAccountEditor, setShowAccountEditor] = React.useState(false);
  const [expireTarget, setExpireTarget] = React.useState<PayingSubscription | null>(null);
  const [expireMode, setExpireMode] = React.useState<'now' | 'period_end'>('now');
  const [expireReason, setExpireReason] = React.useState('');
  const [peopleSubscriptionId, setPeopleSubscriptionId] = React.useState<string | null>(null);
  const [peopleInviteOnOpen, setPeopleInviteOnOpen] = React.useState(false);
  const [settingsSubscriptionId, setSettingsSubscriptionId] = React.useState<string | null>(null);
  const [openSubscriptionId, setOpenSubscriptionId] = React.useState<string | null>(null);
  const [openLevelFeaturesId, setOpenLevelFeaturesId] = React.useState<string | null>(null);
  const [openAccountFeaturesId, setOpenAccountFeaturesId] = React.useState<string | null>(null);
  const [planDrafts, setPlanDrafts] = React.useState<Record<string, string>>({});
  const [accountQuery, setAccountQuery] = React.useState('');
  const [visibleCount, setVisibleCount] = React.useState(40);
  const [portalBusyId, setPortalBusyId] = React.useState<string | null>(null);
  const [upgradeBusyKey, setUpgradeBusyKey] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    if (!apiBase || !accessToken) {
      return;
    }
    const path = mode === 'levels'
      ? '/api/v1/admin/subscription-levels'
      : mode === 'mine'
        ? '/api/v1/account/subscriptions'
        : mode === 'paying'
          ? '/api/v1/admin/paying-subscriptions'
          : '/api/v1/admin/expired-subscriptions';
    const response = await fetch(`${apiBase}${path}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      const detail = typeof body?.detail === 'string' ? body.detail : text.loadError;
      if (response.status === 403 && /admin role is required/i.test(detail)) {
        const denied = new Error(detail) as Error & { staffDenied?: boolean };
        denied.staffDenied = true;
        throw denied;
      }
      throw new Error(detail);
    }
    if (mode === 'levels') {
      setLevels(Array.isArray(body.levels) ? body.levels : []);
      if (Array.isArray(body.features) && body.features.length) {
        setFeatures(body.features);
      }
    } else {
      setRows(Array.isArray(body.subscriptions) ? body.subscriptions : []);
    }
    if (mode === 'paying' || mode === 'expired') {
      const levelResponse = await fetch(`${apiBase}/api/v1/admin/subscription-levels`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const levelBody = await levelResponse.json().catch(() => ({}));
      if (levelResponse.ok) {
        setLevels(Array.isArray(levelBody.levels) ? levelBody.levels : []);
        if (Array.isArray(levelBody.features) && levelBody.features.length) {
          setFeatures(levelBody.features);
        }
      }
    }
  }, [accessToken, apiBase, mode, text.loadError]);

  React.useEffect(() => {
    let cancelled = false;
    void load().catch((loadError: unknown) => {
      if (cancelled) {
        return;
      }
      if (loadError && typeof loadError === 'object' && (loadError as { staffDenied?: boolean }).staffDenied) {
        onAccessDeniedRef.current?.();
        return;
      }
      setError(loadError instanceof Error ? loadError.message : text.loadError);
    });
    return () => {
      cancelled = true;
    };
  }, [load, text.loadError]);

  React.useEffect(() => {
    setNotice(null);
    setError(null);
    setShowLevelEditor(false);
    setShowAccountEditor(false);
    setOpenSubscriptionId(null);
    setEditingId(null);
    setEditingSubscriptionId(null);
  }, [mode]);

  const resetLevelEditor = () => {
    setDraftId('');
    setDraftName('');
    setDraftPrice('');
    setDraftEntitlements([]);
    setEditingId(null);
    setShowLevelEditor(false);
  };

  const resetAccountEditor = () => {
    setAccountName('');
    setAdminEmail('');
    setPlanId('basic');
    setEditingSubscriptionId(null);
    setShowAccountEditor(false);
  };

  const openLevelEditor = (level?: SubscriptionLevel) => {
    if (level) {
      setEditingId(level.level_id);
      setDraftId(level.level_id);
      setDraftName(level.name);
      setDraftPrice(level.monthly_price_usd == null ? '' : String(level.monthly_price_usd));
      setDraftEntitlements([...(level.entitlements || [])]);
      setNotice(null);
      setOpenLevelFeaturesId(level.level_id);
    } else {
      setEditingId(null);
      setDraftId('');
      setDraftName('');
      setDraftPrice('');
      setDraftEntitlements(FEATURE_CATALOG.slice(0, 8).map((entry) => entry.key));
      setNotice(null);
    }
    setShowLevelEditor(true);
    setError(null);
    const anchor = level ? `level-editor-${level.level_id}` : 'level-editor-new';
    window.setTimeout(() => {
      const node = document.getElementById(anchor);
      if (node instanceof HTMLElement) {
        scrollElementWithinConsole(node);
      }
    }, 40);
  };

  const toggleEntitlement = (key: string) => {
    setDraftEntitlements((current) => (
      current.includes(key)
        ? current.filter((item) => item !== key)
        : [...current, key]
    ));
  };

  const saveLevel = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!apiBase || !accessToken) {
      return;
    }
    const price = draftPrice.trim() === '' ? null : Number(draftPrice);
    const payload = {
      level_id: editingId || draftId,
      name: draftName,
      monthly_price_usd: price,
      entitlements: draftEntitlements,
    };
    const response = await fetch(
      editingId
        ? `${apiBase}/api/v1/admin/subscription-levels/${encodeURIComponent(editingId)}`
        : `${apiBase}/api/v1/admin/subscription-levels`,
      {
        method: editingId ? 'PUT' : 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      },
    );
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      setError(body?.detail || text.loadError);
      return;
    }
    const resynced = Number(body?.accounts_resynced || 0);
    setNotice(resynced > 0 ? `${text.saved} ${text.resynced(resynced)}` : text.saved);
    resetLevelEditor();
    await load();
  };

  const removeLevel = async (levelId: string) => {
    if (!apiBase || !accessToken) {
      return;
    }
    const response = await fetch(`${apiBase}/api/v1/admin/subscription-levels/${encodeURIComponent(levelId)}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      setError(body?.detail || text.loadError);
      return;
    }
    setNotice(text.saved);
    await load();
  };

  const saveSubscription = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!apiBase || !accessToken) {
      return;
    }
    const response = await fetch(
      editingSubscriptionId
        ? `${apiBase}/api/v1/admin/paying-subscriptions/${encodeURIComponent(editingSubscriptionId)}`
        : `${apiBase}/api/v1/admin/paying-subscriptions`,
      {
        method: editingSubscriptionId ? 'PUT' : 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          account_name: accountName,
          email: adminEmail,
          tier_id: planId,
        }),
      },
    );
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      setError(body?.detail || text.loadError);
      return;
    }
    setNotice(body?.subscription?.rbac_updated === false ? text.rbacMissing : text.saved);
    resetAccountEditor();
    await load();
  };

  const confirmExpire = async () => {
    if (!apiBase || !accessToken || !expireTarget) {
      return;
    }
    const response = await fetch(
      `${apiBase}/api/v1/admin/paying-subscriptions/${encodeURIComponent(expireTarget.subscription_id)}/expire`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ mode: expireMode, reason: expireReason }),
      },
    );
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      setError(body?.detail || text.loadError);
      return;
    }
    setNotice(text.saved);
    setExpireTarget(null);
    setExpireReason('');
    setExpireMode('now');
    await load();
  };

  const title = mode === 'levels'
    ? text.levelsTitle
    : mode === 'paying'
      ? text.payingTitle
      : mode === 'mine'
        ? text.mineTitle
        : text.expiredTitle;

  const changeSubscription = async (row: PayingSubscription, action: 'plan' | 'suspend' | 'resume') => {
    if (!apiBase || !accessToken) {
      return;
    }
    const response = await fetch(
      `${apiBase}/api/v1/admin/paying-subscriptions/${encodeURIComponent(row.subscription_id)}/status`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action,
          tier_id: planDrafts[row.subscription_id] || row.tier_id || 'basic',
        }),
      },
    );
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      setError(body?.detail || text.loadError);
      return;
    }
    setNotice(action === 'suspend' ? text.suspendedNotice : action === 'resume' ? text.resumedNotice : text.planChanged);
    await load();
  };
  const catalog = features.length ? features : FEATURE_CATALOG;
  const plansIncluding = (featureKey: string) => levels
    .filter((level) => (level.entitlements || []).includes(featureKey))
    .map((level) => level.name);
  const jobLabel = (job?: string) => {
    if (job === 'company_admin') return text.jobAdmin;
    if (job === 'viewer') return text.jobViewer;
    return text.jobContributor;
  };
  const isMineAdmin = (row: PayingSubscription) => row.job === 'company_admin';
  const normalizeTierId = (tierId?: string | null): PricingTierId | null => {
    const tier = String(tierId || '').toLowerCase();
    if (tier === 'basic' || tier === 'advanced' || tier === 'enterprise') {
      return tier;
    }
    return null;
  };
  const openMineBillingPortal = async (row: PayingSubscription) => {
    if (!apiBase || !accessToken) {
      return;
    }
    setPortalBusyId(row.subscription_id);
    setError(null);
    try {
      const response = await fetch(`${apiBase}/api/v1/billing/portal-session`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          return_url: `${window.location.origin}/console/my-subscriptions`,
          company_id: row.company_id ?? undefined,
        }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(typeof body?.detail === 'string' ? body.detail : text.loadError);
      }
      if (body?.url) {
        window.location.assign(String(body.url));
        return;
      }
      throw new Error(text.loadError);
    } catch (err) {
      setError(err instanceof Error ? err.message : text.loadError);
    } finally {
      setPortalBusyId(null);
    }
  };
  const startMineUpgrade = async (row: PayingSubscription, tierId: PricingTierId) => {
    if (!apiBase) {
      return;
    }
    if (tierId === 'enterprise') {
      window.location.assign('mailto:operations@nextcompli.com?subject=CompliComplete%20quote');
      return;
    }
    const busyKey = `${row.subscription_id}:${tierId}`;
    setUpgradeBusyKey(busyKey);
    setError(null);
    try {
      const email = String(row.email || '').trim().toLowerCase();
      if (email && accessToken) {
        const checkoutResponse = await fetch(`${apiBase}/api/v1/billing/checkout-session`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            tier_id: tierId,
            email,
            company_name: row.company_name || 'NextCompli customer',
            company_id: row.company_id ?? undefined,
            subscription_id: row.subscription_id,
            success_url: `${window.location.origin}/console/my-subscriptions?checkout=success`,
            cancel_url: `${window.location.origin}/console/my-subscriptions?checkout=cancel`,
            ...getAttribution(),
          }),
        });
        const checkoutBody = await checkoutResponse.json().catch(() => ({}));
        if (checkoutResponse.ok && checkoutBody?.url) {
          window.location.assign(String(checkoutBody.url));
          return;
        }
      }
      await openMineBillingPortal(row);
    } catch (err) {
      setError(err instanceof Error ? err.message : text.loadError);
    } finally {
      setUpgradeBusyKey(null);
    }
  };
  const renderLevelEditor = (anchorId: string) => {
    const seenTerms = new Set<DodGlossaryTermId>();
    return (
      <form id={anchorId} className="sub-catalog__editor" onSubmit={(event) => void saveLevel(event)}>
        <h3>{editingId ? text.save : text.add}</h3>
        {editingId && levels.find((level) => level.level_id === editingId)?.canonical ? (
          <p className="sub-catalog__meta">{text.canonical}</p>
        ) : null}
        <div className="sub-catalog__fields">
          {editingId ? null : (
            <label>
              <span>{text.id}</span>
              <input aria-label={text.id} value={draftId} onChange={(event) => setDraftId(event.target.value)} required />
            </label>
          )}
          <label>
            <span>{text.name}</span>
            <input aria-label={text.name} value={draftName} onChange={(event) => setDraftName(event.target.value)} required />
          </label>
          <label>
            <span>{text.price}</span>
            <input
              aria-label={text.price}
              value={draftPrice}
              onChange={(event) => setDraftPrice(event.target.value)}
              inputMode="numeric"
              placeholder={text.contactPrice}
            />
          </label>
        </div>
        <div className="sub-catalog__feature-block">
          <h3>{text.featuresTitle}</h3>
          <p>{text.featuresHelp}</p>
          {FEATURE_CATEGORIES.map((category) => {
            const group = catalog.filter((entry) => entry.category === category);
            if (!group.length) {
              return null;
            }
            return (
              <div className="sub-catalog__feature-group" key={category}>
                <h4>{category}</h4>
                <div className="sub-catalog__feature-list">
                  {group.map((entry) => {
                    const planNames = plansIncluding(entry.key);
                    return (
                      <div className="sub-catalog__feature" key={entry.key}>
                        <label>
                          <input
                            type="checkbox"
                            aria-label={entry.name}
                            checked={draftEntitlements.includes(entry.key)}
                            onChange={() => toggleEntitlement(entry.key)}
                          />
                          <span>
                            <strong><DodTermText text={entry.name} language={language} seen={seenTerms} /></strong>
                            <DodTermText text={entry.description} language={language} seen={seenTerms} />
                            {planNames.length ? (
                              <span className="sub-catalog__plan-badges">
                                {planNames.map((planName) => (
                                  <span className="sub-catalog__plan-badge" key={planName}>{planName}</span>
                                ))}
                              </span>
                            ) : null}
                            <small>{entry.permission}</small>
                          </span>
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        <div className="sub-catalog__actions">
          <button type="submit" className="btn btn-primary">{text.save}</button>
          <button type="button" className="btn btn-secondary" onClick={resetLevelEditor}>
            {text.cancelEdit}
          </button>
        </div>
      </form>
    );
  };
  const renderAccountEditor = (anchorId: string) => (
    <form id={anchorId} className="sub-catalog__editor" onSubmit={(event) => void saveSubscription(event)}>
      <h3>{editingSubscriptionId ? text.saveAccount : text.newAccount}</h3>
      <p className="sub-catalog__meta">{text.peopleHelp}</p>
      <div className="sub-catalog__fields">
        <label>
          <span>{text.accountName}</span>
          <input
            aria-label={text.accountName}
            value={accountName}
            onChange={(event) => setAccountName(event.target.value)}
            required
          />
        </label>
        <label>
          <span>{text.adminEmail}</span>
          <input
            aria-label={text.adminEmail}
            type="email"
            value={adminEmail}
            onChange={(event) => setAdminEmail(event.target.value)}
            required
          />
        </label>
        <label>
          <span>{text.planChoice}</span>
          <select aria-label={text.planChoice} value={planId} onChange={(event) => setPlanId(event.target.value)}>
            {(levels.length ? levels : [{ level_id: 'basic', name: 'CompliCore' }]).map((level) => (
              <option key={level.level_id} value={level.level_id}>{level.name}</option>
            ))}
          </select>
        </label>
      </div>
      <div className="sub-catalog__actions">
        <button type="submit" className="btn btn-primary">{text.saveAccount}</button>
        <button type="button" className="btn btn-secondary" onClick={resetAccountEditor}>
          {text.cancelEdit}
        </button>
      </div>
    </form>
  );
  const accountQueryNormalized = accountQuery.trim().toLowerCase();
  const filteredRows = rows.filter((row) => {
    if (!accountQueryNormalized) {
      return true;
    }
    return [row.company_name, row.email, row.tier_name, row.status, row.tier_id]
      .join(' ')
      .toLowerCase()
      .includes(accountQueryNormalized);
  });
  const visibleRows = filteredRows.slice(0, visibleCount);

  return (
    <section className="dashboard-settings-sections dashboard-settings-sections-wide" aria-label={title}>
      <div className="sub-catalog">
        <div className="sub-catalog__hero">
          <div>
            <h2>{title}</h2>
            {mode === 'mine' ? <p className="sub-catalog__meta">{text.mineHelp}</p> : null}
          </div>
          {mode === 'levels' ? (
            <button type="button" className="btn btn-primary" onClick={() => openLevelEditor()}>
              {text.add}
            </button>
          ) : null}
          {mode === 'paying' ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                resetAccountEditor();
                setShowAccountEditor(true);
                setEditingSubscriptionId(null);
                setOpenSubscriptionId(null);
                setError(null);
                setNotice(null);
                window.setTimeout(() => {
                  const node = document.getElementById('account-editor-new');
                  if (node instanceof HTMLElement) {
                    scrollElementWithinConsole(node);
                  }
                }, 40);
              }}
            >
              {text.newAccount}
            </button>
          ) : null}
        </div>
        {mode === 'paying' && showAccountEditor && !editingSubscriptionId
          ? renderAccountEditor('account-editor-new')
          : null}

        {error ? <p className="sub-catalog__error" role="alert">{error}</p> : null}
        {notice ? <p className="sub-catalog__notice" role="status">{notice}</p> : null}

        {mode === 'levels' ? (
          <>
            {levels.length === 0 ? (
              <div className="sub-catalog__empty">{text.emptyLevels}</div>
            ) : (
              <div className="sub-catalog__grid">
                {levels.map((level) => {
                  const entitlementKeys = level.entitlements || [];
                  return (
                    <article
                      key={level.level_id}
                      className={`sub-catalog__card${editingId === level.level_id ? ' is-editing' : ''}`}
                    >
                      <div className="sub-catalog__card-top">
                        <div>
                          <h3>{level.name}</h3>
                          <p className="sub-catalog__price">
                            {formatMoney(level.monthly_price_usd, language, text.contactPrice)}
                            {level.monthly_price_usd != null ? <span>{text.perMonth}</span> : null}
                          </p>
                        </div>
                        {level.canonical ? (
                          <span className="sub-catalog__pill sub-catalog__pill--canonical">Catalog</span>
                        ) : null}
                      </div>
                      <p className="sub-catalog__meta">
                        <button
                          type="button"
                          className="sub-catalog__text-button"
                          aria-expanded={openLevelFeaturesId === level.level_id}
                          onClick={() => setOpenLevelFeaturesId(
                            openLevelFeaturesId === level.level_id ? null : level.level_id,
                          )}
                        >
                          {text.featuresCount(entitlementKeys.length)}
                        </button>
                      </p>
                      {openLevelFeaturesId === level.level_id ? (
                        <ul className="sub-catalog__chips">
                          {entitlementKeys.map((key) => (
                            <li key={key}>
                              <FeatureMark featureKey={key} />
                              <DodTermText text={featureLabel(key)} language={language} />
                            </li>
                          ))}
                        </ul>
                      ) : null}
                      <div className="sub-catalog__actions">
                        <button type="button" className="btn btn-secondary" onClick={() => openLevelEditor(level)}>
                          {text.edit}
                        </button>
                        {level.canonical ? null : (
                          <button type="button" className="btn btn-secondary" onClick={() => void removeLevel(level.level_id)}>
                            {text.delete}
                          </button>
                        )}
                      </div>
                      {editingId === level.level_id && showLevelEditor
                        ? renderLevelEditor(`level-editor-${level.level_id}`)
                        : null}
                    </article>
                  );
                })}
              </div>
            )}

            {showLevelEditor && !editingId ? renderLevelEditor('level-editor-new') : null}
          </>
        ) : (
          <>
            {mode !== 'levels' ? (
              <label className="sub-catalog__search">
                <svg className="sub-catalog__search-icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                  <path
                    fill="currentColor"
                    d="M11.5 10.4a5.5 5.5 0 1 0-1.1 1.1l3 3a.75.75 0 1 0 1.06-1.06l-3-3ZM6.75 10.5a3.75 3.75 0 1 1 0-7.5 3.75 3.75 0 0 1 0 7.5Z"
                  />
                </svg>
                <span className="visually-hidden">{text.searchAccounts}</span>
                <input
                  aria-label={text.searchAccounts}
                  placeholder={text.searchAccounts}
                  value={accountQuery}
                  onChange={(event) => {
                    setAccountQuery(event.target.value);
                    setVisibleCount(40);
                  }}
                />
              </label>
            ) : null}
            {filteredRows.length === 0 ? (
              <div className="sub-catalog__empty">
                {accountQuery.trim() && rows.length
                  ? text.searchEmpty
                  : mode === 'paying'
                    ? text.emptyPaying
                    : mode === 'mine'
                      ? text.emptyMine
                      : text.emptyExpired}
              </div>
            ) : (
              <div className="sub-catalog__list">
                <p className="sub-catalog__meta">{text.showingCount(visibleRows.length, filteredRows.length)}</p>
                <div className="sub-catalog__list-head" aria-hidden="true">
                  <span>{text.colAccount}</span>
                  <span>{text.colPlan}</span>
                  <span>{text.colRenews}</span>
                  <span>{text.colMembers}</span>
                  <span />
                  <span>{text.colStatus}</span>
                </div>
                {visibleRows.map((row) => {
                  const featureKeys = row.entitlement_features?.length
                    ? row.entitlement_features
                    : (levels.find((level) => level.level_id === row.tier_id)?.entitlements || []);
                  const open = openSubscriptionId === row.subscription_id;
                  const suspended = String(row.status || '').toLowerCase() === 'suspended';
                  const ended = mode === 'expired' || ['canceled', 'cancelled', 'incomplete_expired', 'unpaid', 'past_due', 'incomplete'].includes(String(row.status || '').toLowerCase());
                  const statusPill = suspended
                    ? 'sub-catalog__pill--ending'
                    : ended
                    ? 'sub-catalog__pill--ended'
                    : row.cancel_at_period_end
                      ? 'sub-catalog__pill--ending'
                      : 'sub-catalog__pill--live';
                  const statusLabel = suspended
                    ? text.suspendedLabel
                    : ended
                      ? text.expired
                      : row.cancel_at_period_end
                        ? text.endingSoon
                        : text.active;
                  const acceptedMembers = (row.members || []).filter((member) => {
                    const status = String(member.invite_status || '').toLowerCase();
                    return status !== 'pending' && status !== 'expired';
                  });
                  const pendingCount = Number(row.pending_invite_count || 0);
                  const acceptedCount = Number(row.member_count ?? acceptedMembers.length);
                  const memberPreview = acceptedMembers.slice(0, 3);
                  const memberSummary = (() => {
                    if (acceptedCount === 0 && pendingCount === 0) {
                      return text.membersEmpty;
                    }
                    const emails = memberPreview.map((member) => member.email).filter(Boolean);
                    const leftover = Math.max(0, acceptedCount - emails.length);
                    const parts = [...emails];
                    if (leftover > 0) {
                      parts.push(text.membersMore(leftover));
                    }
                    if (pendingCount > 0) {
                      parts.push(text.membersPending(pendingCount));
                    }
                    return parts.join(' · ') || text.membersEmpty;
                  })();
                  return (
                    <article
                      key={row.subscription_id}
                      className={`sub-catalog__card sub-catalog__row${open ? ' is-open' : ''}`}
                    >
                      <div className="sub-catalog__account-line">
                      <button
                        type="button"
                        className="sub-catalog__accordion-toggle sub-catalog__row-toggle"
                        aria-expanded={open}
                        onClick={() => setOpenSubscriptionId(open ? null : row.subscription_id)}
                      >
                        <strong>{row.company_name || text.account}</strong>
                        <span>{row.tier_name || row.tier_id}</span>
                        <span title={`${text.colRenews}: ${formatRenewal(row.current_period_end, language)}`}>
                          {formatRenewal(row.current_period_end, language)}
                        </span>
                        <span className="sub-catalog__members-cell" title={memberSummary}>
                          {memberSummary}
                        </span>
                        <span className={`sub-catalog__pill ${statusPill}`}>{statusLabel}</span>
                      </button>
                      {open ? (
                      <div className="sub-catalog__icon-row">
                        {mode === 'mine' && row.job ? (
                          <span className="sub-catalog__icon-note">{jobLabel(row.job)}</span>
                        ) : null}
                        {mode === 'mine' ? null : (
                          <>
                            <button
                              type="button"
                              className="sub-catalog__icon-btn"
                              aria-label={text.addPerson}
                              aria-pressed={false}
                              onClick={() => {
                                setPeopleInviteOnOpen(true);
                                setPeopleSubscriptionId(row.subscription_id);
                                setExpireTarget(null);
                                setShowAccountEditor(false);
                              }}
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <circle cx="9" cy="8" r="3" />
                                <path d="M3.5 19c.8-2.6 2.8-4 5.5-4s4.7 1.4 5.5 4" />
                                <path d="M19 8v6M16 11h6" />
                              </svg>
                              <span>{text.addShort}</span>
                            </button>
                            <button
                              type="button"
                              className={`sub-catalog__icon-btn${peopleSubscriptionId === row.subscription_id ? ' is-on' : ''}`}
                              aria-label={text.people}
                              aria-pressed={peopleSubscriptionId === row.subscription_id}
                              onClick={() => {
                                setPeopleInviteOnOpen(false);
                                setPeopleSubscriptionId(peopleSubscriptionId === row.subscription_id ? null : row.subscription_id);
                                setExpireTarget(null);
                                setShowAccountEditor(false);
                              }}
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <circle cx="9" cy="8" r="3" />
                                <path d="M3 19c.9-2.8 3-4.2 6-4.2s5.1 1.4 6 4.2" />
                                <circle cx="17" cy="9" r="2.2" />
                                <path d="M16.2 14.2c1.8.3 3.2 1.3 4 2.8" />
                              </svg>
                              <span>{text.people}</span>
                              {pendingCount > 0 ? <em className="sub-catalog__icon-badge">{pendingCount}</em> : null}
                            </button>
                          </>
                        )}
                        {featureKeys.length ? (
                          <button
                            type="button"
                            className={`sub-catalog__icon-btn${openAccountFeaturesId === row.subscription_id ? ' is-on' : ''}`}
                            aria-label={text.featuresCount(featureKeys.length)}
                            aria-pressed={openAccountFeaturesId === row.subscription_id}
                            onClick={() => setOpenAccountFeaturesId(
                              openAccountFeaturesId === row.subscription_id ? null : row.subscription_id,
                            )}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <rect x="4" y="4" width="6.5" height="6.5" rx="1.2" />
                              <rect x="13.5" y="4" width="6.5" height="6.5" rx="1.2" />
                              <rect x="4" y="13.5" width="6.5" height="6.5" rx="1.2" />
                              <rect x="13.5" y="13.5" width="6.5" height="6.5" rx="1.2" />
                            </svg>
                            <span>{text.featuresCount(featureKeys.length)}</span>
                          </button>
                        ) : null}
                        {mode === 'mine' ? null : (
                          <button
                            type="button"
                            className={`sub-catalog__icon-btn${settingsSubscriptionId === row.subscription_id ? ' is-on' : ''}`}
                            aria-label={text.accountSettings}
                            aria-pressed={settingsSubscriptionId === row.subscription_id}
                            onClick={() => setSettingsSubscriptionId(
                              settingsSubscriptionId === row.subscription_id ? null : row.subscription_id,
                            )}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <circle cx="12" cy="12" r="2.6" />
                              <path d="M19.4 15a1.7 1.7 0 0 0 .34 1.87l.03.03a2 2 0 0 1-2.83 2.83l-.03-.03a1.7 1.7 0 0 0-1.87-.34 1.7 1.7 0 0 0-1.04 1.56V21a2 2 0 0 1-4 0v-.05a1.7 1.7 0 0 0-1.04-1.56 1.7 1.7 0 0 0-1.87.34l-.03.03a2 2 0 1 1-2.83-2.83l.03-.03A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-1.56-1.04H3a2 2 0 0 1 0-4h.04A1.7 1.7 0 0 0 4.6 8.92a1.7 1.7 0 0 0-.34-1.87l-.03-.03a2 2 0 1 1 2.83-2.83l.03.03a1.7 1.7 0 0 0 1.87.34H9a1.7 1.7 0 0 0 1-1.56V3a2 2 0 0 1 4 0v.04a1.7 1.7 0 0 0 1.04 1.56h.01a1.7 1.7 0 0 0 1.87-.34l.03-.03a2 2 0 1 1 2.83 2.83l-.03.03a1.7 1.7 0 0 0-.34 1.87v.01A1.7 1.7 0 0 0 20.96 10H21a2 2 0 0 1 0 4h-.04A1.7 1.7 0 0 0 19.4 15Z" />
                            </svg>
                            <span>{text.settingsShort}</span>
                          </button>
                        )}
                      </div>
                      ) : null}
                      </div>
                      {open ? (
                      <>
                      {mode !== 'mine' && peopleSubscriptionId === row.subscription_id ? (
                        <SubscriptionTeamPanel
                          language={language}
                          gatewayBaseUrl={apiBase}
                          accessToken={accessToken}
                          mode="subscription"
                          subscriptionId={row.subscription_id}
                          key={`${row.subscription_id}:${peopleInviteOnOpen ? 'invite' : 'people'}`}
                          startInvite={peopleInviteOnOpen}
                          onClose={() => {
                            setPeopleSubscriptionId(null);
                            setPeopleInviteOnOpen(false);
                          }}
                        />
                      ) : null}
                      {openAccountFeaturesId === row.subscription_id ? (
                        <ul className="sub-catalog__chips">
                          {featureKeys.map((key) => (
                            <li key={key}>
                              <FeatureMark featureKey={key} />
                              <DodTermText text={featureLabel(key)} language={language} />
                            </li>
                          ))}
                        </ul>
                      ) : null}
                      {mode === 'mine' ? (
                        <div className="sub-catalog__settings">
                          {isMineAdmin(row) ? (
                            <>
                              <p className="sub-catalog__meta">{text.upgradePlan}</p>
                              <div className="sub-catalog__icon-row">
                                {PRICING_TIERS.map((tier) => {
                                  const currentTier = normalizeTierId(row.tier_id);
                                  const isCurrent = currentTier === tier.id;
                                  const busyKey = `${row.subscription_id}:${tier.id}`;
                                  const label = isCurrent
                                    ? text.currentPlan
                                    : tier.id === 'enterprise'
                                      ? text.contactQuote
                                      : text.upgradeTo(tier.marketingLabel);
                                  return (
                                    <button
                                      key={tier.id}
                                      type="button"
                                      className="sub-catalog__icon-btn sub-catalog__icon-btn--wide"
                                      disabled={isCurrent || portalBusyId === row.subscription_id || upgradeBusyKey === busyKey}
                                      onClick={() => void startMineUpgrade(row, tier.id)}
                                    >
                                      <span>
                                        {upgradeBusyKey === busyKey ? text.openingBilling : label}
                                      </span>
                                    </button>
                                  );
                                })}
                                <button
                                  type="button"
                                  className="sub-catalog__icon-btn sub-catalog__icon-btn--wide"
                                  disabled={portalBusyId === row.subscription_id || Boolean(upgradeBusyKey)}
                                  onClick={() => void openMineBillingPortal(row)}
                                >
                                  <span>
                                    {portalBusyId === row.subscription_id
                                      ? text.openingBilling
                                      : text.manageBilling}
                                  </span>
                                </button>
                              </div>
                            </>
                          ) : (
                            <p className="sub-catalog__meta">{text.mineBillingViewer}</p>
                          )}
                        </div>
                      ) : null}
                      {mode !== 'mine' && settingsSubscriptionId === row.subscription_id ? (
                      <div className="sub-catalog__settings">
                      {row.source === 'operator-backfill' ? (
                        <p className="sub-catalog__meta">{text.backfill}</p>
                      ) : null}
                      <p className="sub-catalog__meta">{text.planHelp}</p>
                      <div className="sub-catalog__fields">
                        <label>
                          <span>{text.planChoice}</span>
                          <select
                            aria-label={text.planChoice}
                            value={planDrafts[row.subscription_id] || row.tier_id || 'basic'}
                            onChange={(event) => setPlanDrafts((current) => ({
                              ...current,
                              [row.subscription_id]: event.target.value,
                            }))}
                          >
                            {(levels.length ? levels : [{ level_id: row.tier_id || 'basic', name: row.tier_name || 'CompliCore' }]).map((level) => (
                              <option key={level.level_id} value={level.level_id}>{level.name}</option>
                            ))}
                          </select>
                        </label>
                      </div>
                      <div className="sub-catalog__icon-row">
                        <button type="button" className="sub-catalog__icon-btn sub-catalog__icon-btn--wide" onClick={() => void changeSubscription(row, 'plan')}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M7 7h11M15 4l3 3-3 3M17 17H6M9 14l-3 3 3 3" /></svg>
                          <span>{text.changePlan}</span>
                        </button>
                        {suspended || mode === 'expired' ? (
                          <button type="button" className="sub-catalog__icon-btn sub-catalog__icon-btn--wide" onClick={() => void changeSubscription(row, 'resume')}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M8 6v12l10-6L8 6Z" /></svg>
                            <span>{text.resume}</span>
                          </button>
                        ) : (
                          <button type="button" className="sub-catalog__icon-btn sub-catalog__icon-btn--wide" onClick={() => void changeSubscription(row, 'suspend')}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M9 6v12M15 6v12" /></svg>
                            <span>{text.suspend}</span>
                          </button>
                        )}
                        {mode === 'paying' ? (
                          <>
                            <button
                              type="button"
                              className="sub-catalog__icon-btn sub-catalog__icon-btn--wide"
                              onClick={() => {
                                setEditingSubscriptionId(row.subscription_id);
                                setAccountName(row.company_name);
                                setAdminEmail(row.email);
                                setPlanId(row.tier_id || 'basic');
                                setShowAccountEditor(true);
                                setExpireTarget(null);
                                setOpenSubscriptionId(row.subscription_id);
                                window.setTimeout(() => {
                                  const node = document.getElementById(`account-editor-${row.subscription_id}`);
                                  if (node instanceof HTMLElement) {
                                    scrollElementWithinConsole(node);
                                  }
                                }, 40);
                              }}
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M4 20h4l10-10-4-4L4 16v4Z" /><path d="m12.5 7.5 4 4" /></svg>
                              <span>{text.edit}</span>
                            </button>
                            <button
                              type="button"
                              className="sub-catalog__icon-btn sub-catalog__icon-btn--wide"
                              onClick={() => {
                                setExpireTarget(row);
                                setExpireMode('now');
                                setExpireReason('');
                                setShowAccountEditor(false);
                              }}
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><circle cx="12" cy="12" r="8" /><path d="M9 9l6 6M15 9l-6 6" /></svg>
                              <span>{text.endNow}</span>
                            </button>
                            <button
                              type="button"
                              className="sub-catalog__icon-btn sub-catalog__icon-btn--wide"
                              onClick={() => {
                                setExpireTarget(row);
                                setExpireMode('period_end');
                                setExpireReason('');
                                setShowAccountEditor(false);
                              }}
                            >
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><rect x="4" y="5" width="16" height="15" rx="2" /><path d="M8 3v4M16 3v4M4 10h16" /></svg>
                              <span>{text.endPeriod}</span>
                            </button>
                          </>
                        ) : null}
                      </div>
                      </div>
                      ) : null}
                      {expireTarget?.subscription_id === row.subscription_id ? (
                        <div className="sub-catalog__expire-box">
                          <p>{expireMode === 'now' ? text.expireConfirmNow : text.expireConfirmPeriod}</p>
                          <label>
                            <span className="sub-catalog__meta">{text.expireReason}</span>
                            <input
                              aria-label={text.expireReason}
                              value={expireReason}
                              onChange={(event) => setExpireReason(event.target.value)}
                            />
                          </label>
                          <div className="sub-catalog__actions">
                            <button type="button" className="btn btn-primary" onClick={() => void confirmExpire()}>
                              {text.confirmExpire}
                            </button>
                            <button type="button" className="btn btn-secondary" onClick={() => setExpireTarget(null)}>
                              {text.cancelEdit}
                            </button>
                          </div>
                        </div>
                      ) : null}
                      {mode === 'paying' && showAccountEditor && editingSubscriptionId === row.subscription_id
                        ? renderAccountEditor(`account-editor-${row.subscription_id}`)
                        : null}
                      </>
                      ) : null}
                    </article>
                  );
                })}
                {visibleRows.length < filteredRows.length ? (
                  <button type="button" className="btn btn-secondary" onClick={() => setVisibleCount((count) => count + 40)}>
                    {text.showMore}
                  </button>
                ) : null}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default SubscriptionCatalogPanel;
